import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import AgentPreviewTab from "@/components/agent/tabs/AgentPreviewTab";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { usePublicAgent } from "@/hooks/agent/usePublicAgent";
import { usePublicAgentAuth } from "@/hooks/agent/usePublicAgentAuth";
import { PublicAgentLayout } from "@/components/agent/public/PublicAgentLayout";
import { PublicAgentError } from "@/components/agent/public/PublicAgentError";
import { PublicAgentLoading } from "@/components/agent/public/PublicAgentLoading";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

// Directly read env vars here - this component is part of the main bundle
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

const PublicAgentChat = () => {
  const { agentUrl, publicId } = useParams<{
    agentUrl: string;
    publicId: string;
  }>();
  const [searchParams] = useSearchParams();
  const isEmbedded = searchParams.get("embed") === "true";
  const hideHeader = searchParams.get("hideHeader") === "true" || isEmbedded;
  const hideNavBar = searchParams.get("hideNavBar") === "true";
  const navigate = useNavigate();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  // Fetch agent data
  const { agent, organization, isLoading, error } = usePublicAgent(
    publicId,
    agentUrl,
  );

  useEffect(() => {
    const myChannel = supabase.channel("org-tracking");
    // Subscribe to the Channel
    myChannel
      .on("broadcast", { event: "agent_url_changed" }, (event) => {
        const changedAgentUrl = event.payload.agent_url;
        navigate(`/${encodeURIComponent(changedAgentUrl)}/${publicId}`);
      })
      .subscribe();

    return () => {
      myChannel.unsubscribe();
    };
  }, [navigate, publicId, agentUrl]);

  // Handle authentication
  const { userInfo, needsAuth } = usePublicAgentAuth(publicId, isEmbedded);

  // Handle redirection to auth page if needed
  if (needsAuth) {
    console.log("No user data found, redirecting to auth page");
    navigate(`/auth/${publicId}`);
    return null;
  }

  // Handle loading state
  if (isLoading) {
    return <PublicAgentLoading />;
  }

  // Handle error state
  if (error || !agent) {
    return <PublicAgentError error={error} isEmbedded={isEmbedded} />;
  }

  const brandColor = organization?.brand_color || "#060520";

  const handleError = (errorMessage: string) => {
    toast({
      variant: "destructive",
      title: "Error",
      description: errorMessage || "Something went wrong. Please try again.",
    });
  };

  // Use appropriate class to ensure proper height based on device and embed status
  const containerClass = "h-full"; // Simplified - let child components handle responsiveness

  // Check if keys are loaded
  if (!supabaseUrl || !supabaseAnonKey) {
    console.error("PublicAgentChat: Supabase URL or Anon Key is missing!");
    // Display an error or loading state, as the app cannot function
    return (
      <PublicAgentError
        error="Application configuration error."
        isEmbedded={isEmbedded}
      />
    );
  }

  // Pass config down if necessary, although ideally hooks should import the initialized client.
  // For now, let's just ensure the component loads the vars. The hooks *should* work if this component loads them.
  console.log("PublicAgentChat: Supabase config loaded successfully.");

  return (
    <PublicAgentLayout
      brandColor={brandColor}
      isEmbedded={isEmbedded}
      hideNavBar={hideNavBar}
    >
      <div className={containerClass}>
        <AgentPreviewTab
          agent={agent}
          organization={organization}
          isPublic={true}
          onError={handleError}
          brandColor={brandColor}
          hideHeader={hideHeader}
          isMobile={isMobile || isEmbedded}
          userInfo={userInfo}
        />
      </div>
    </PublicAgentLayout>
  );
};

export default PublicAgentChat;
